<template>
  <b-tab active @click="refreshTable()">
    <template #title>
      <span class="d-none d-sm-inline-block">
        {{ $t('master.list.rfid.tag.title') }}
      </span>
    </template>
    <div v-if="isTableDataAvailable" class="d-flex">
      <b-col md="auto">
        <span class="fw-light mc-row-check mt-0">
          {{ $t('master.list.rfid.tag.RFIDActive') }}
        </span>
        <div class="fw-bold mc-strong__label">
          {{ totalActiveRFID }}
        </div>
      </b-col>
      <b-col md="auto" class="mx-3">
        <span class="fw-light mc-row-check mt-0">
          {{ $t('master.list.rfid.tag.RFIDActiveWithoutDriver') }}
        </span>
        <div class="fw-bold mc-strong__label">
          {{ totalActiveRFIDWithoutDriver }}
        </div>
      </b-col>
      <b-col>
        <span class="fw-light mc-row-check mt-0">
          {{ $t('master.list.rfid.tag.RFIDInactive') }}
        </span>
        <div class="fw-bold mc-strong__label">
          {{ totalInactiveRFID }}
        </div>
      </b-col>

      <b-dropdown
        variant="outline-primary"
        class="mb-2 ms-2 dropdown-status__rfid"
        :disabled="!selectedRFIDTags.length"
      >
        <template #button-content>
          {{ `${$t('global.update')} Status` }}
          <i class="mdi mdi-chevron-down" />
        </template>
        <b-dropdown-item>
          <McButton
            variant="secondary"
            class="mx-2"
            icon="icon-outline_arrow-right"
            text=""
            @click="showUpdateStatus('A')"
          >
            <template #before-slot>
              {{ $t('master.list.rfid.tag.changeTo') }}
            </template>
            <template #after-slot>
              <span class="mc-status mc-status--green px-1" />{{ $t('global.active') }}
            </template>
          </McButton>
        </b-dropdown-item>
        <b-dropdown-item>
          <McButton
            variant="secondary"
            class="mx-2"
            icon="icon-outline_arrow-right"
            text=""
            @click="showUpdateStatus('I')"
          >
            <template #before-slot>
              {{ $t('master.list.rfid.tag.changeTo') }}
            </template>
            <template #after-slot>
              <span class="mc-status mc-status--red px-1" />{{ $t('global.nonActive') }}
            </template>
          </McButton>
        </b-dropdown-item>
      </b-dropdown>
    </div>
    <!-- Table -->
    <div class="table-responsive mb-5">
      <McGraphqlEditableTable
        v-if="isTableDataAvailable"
        ref="table-ref"
        :fields="fields"
        :apiClient="query"
        :searchable="true"
        :canCreate="false"
        :canDelete="false"
        :idIsString="true"
        :hasMassSelect="true"
        :withFlex="false"
        trackBy="rfid"
        @handleSelectValue="handleSelectTag"
        @handleChange="handleChange"
        @handleEditCancel="handleEditCancel"
      >
        <!-- Slot: Status Column -->
        <template #isActive="{ item }">
          <span v-if="item.isActive.value == 'A'" class="mc-status mc-status--green">
            {{ $t('global.active') }}
          </span>
          <span v-else class="mc-status mc-status--red">{{ $t('global.nonActive') }}</span>
        </template>

        <!-- Slot: Change History Column -->
        <template #history="{ item }">
          <McButton
            variant="secondary"
            :text="$t('master.list.rfid.tag.history')"
            class="mc-btn__sm mc-btn__table d-flex justify-content-center"
            :disabled="isEditing"
            @click="handleShowHistory(item)"
          />
        </template>

        <!-- Slot: Assigned Date Driver Column -->
        <template #assignedDate="{ item }">
          <p class="my-0">
            {{ item.assignedDate ? formatIndexDate(item.assignedDate).date : '-' }}
          </p>
          <p v-if="item.assignedDate" class="text-muted my-0">
            {{ formatIndexDate(item.assignedDate).time }}
          </p>
        </template>
      </McGraphqlEditableTable>

      <!-- Empty State -->
      <div v-show="!isTableDataAvailable" class="empty-table-container">
        <McEmptyState
          :title="$t('master.list.rfid.tag.emptyState')"
          :desc="$t('master.list.rfid.tag.emptyStateDesc')"
          :hasButton="false"
          class="pb-4"
        />
      </div>
    </div>

    <b-modal
      id="modal-update-status"
      class="mc-modal"
      :title="
        $t('master.list.rfid.tag.changeTo') +
        ' ' +
        (updateStatus === 'A' ? $t('global.active') : $t('global.nonActive'))
      "
      body-class="p-3"
      size="md"
      centered
    >
      <div class="card-body mc-modal__filter p-2">
        <p>
          {{ $t('global.text.modal.changeStatus') }}
          <strong>{{ selectedRFIDTags.length }}</strong>
          {{ $t('global.text.modal.changeStatusLength') }}
          <b v-if="updateStatus === 'A'">{{ $t('global.active') }} </b>
          <b v-if="updateStatus === 'I'">{{ $t('global.nonActive') }} </b> ?
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            :text="$t('global.action.cancel')"
            class="me-2"
            @click="cancel()"
          />
          <McButton
            variant="primary"
            :text="
              $t('master.list.rfid.tag.changeTo') +
              ' ' +
              (updateStatus === 'A' ? $t('global.active') : $t('global.nonActive'))
            "
            @click="updateTagStatuses"
          />
        </div>
      </template>
    </b-modal>
  </b-tab>
</template>
<script>
import { mapGetters } from 'vuex'
import McGraphqlEditableTable from '@/components/McGraphqlEditableTable'
import { GET_EMPLOYEES_PAGINATED } from '@/services/api/master/employees'
import McButton from '@/components/McButton'
import McEmptyState from '@/components/McEmptyState'
import { Helper } from '@/mixin/helper'
import { formatIndexDate } from '@/helpers/tzDate'
import { GET_RFIDS, UPDATE_RFID, UPDATE_RFID_STATUSES } from '@/services/api/master/rfid'
export default {
  components: {
    McGraphqlEditableTable,
    McButton,
    McEmptyState,
  },
  mixins: [Helper],
  data() {
    return {
      totalActiveRFID: 0,
      totalActiveRFIDWithoutDriver: 0,
      totalInactiveRFID: 0,
      isTableDataAvailable: true,
      updateStatus: '',
      selectedRFIDTags: [],
      listRFIDTags: [],
      isEditing: false,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
    }),
    fields() {
      return [
        {
          key: 'rfid',
          label: this.$t('master.list.rfid.tag.RFIDNumber'),
          thClass: 'mc-table--50',
          isSortable: true,
          isEditable: false,
        },
        {
          key: 'isActive',
          label: this.$t('master.list.rfid.tag.status'),
          thClass: 'mc-table--170',
          isSortable: true,
          isEditable: true,
          type: 'select-resourceful',
          trackBy: 'value',
          optionsGetter: this.resourcefulQuery.getStatuses,
          optionLabelFn: ({ item }) => item.name,
          getInitialObject: (item) => {
            return {
              value: item.isActive.value,
              name:
                item.isActive.value === 'A'
                  ? this.$t('global.active')
                  : this.$t('global.nonActive'),
            }
          },
          customValueFn: (item) => item.isActive.value ?? '',
          placeholder: this.$t('master.list.rfid.tag.selectStatus'),
          customColumn: true,
          validation: [['required']],
        },
        {
          key: 'driver.fullname',
          label: this.$t('master.list.rfid.tag.driver'),
          thClass: 'mc-table--170',
          isSortable: true,
          isEditable: true,
          type: 'select-resourceful',
          trackBy: 'userId',
          optionsGetter: this.resourcefulQuery.getEmployees,
          optionLabelFn: ({ item }) => item.fullname,
          getInitialObject: (item) => {
            return { userId: item.driver?.userId, fullname: item.driver?.fullname }
          },
          customValueFn: (item) => item.driver?.userId ?? '',
          formatter: (item) => item || '-',
          placeholder: this.$t('master.list.rfid.tag.selectDriver'),
        },
        {
          key: 'description',
          label: this.$t('master.list.rfid.tag.description'),
          thClass: 'mc-table--50',
          isSortable: false,
          isEditable: true,
          formatter: (item) => item || '-',
          validation: 'max:100',
        },
        {
          key: 'assignedDate',
          label: this.$t('master.list.rfid.tag.assignedDate'),
          thClass: 'mc-table--50',
          isSortable: true,
          isEditable: false,
          customColumn: true,
          type: 'date',
        },
        {
          key: 'history',
          label: this.$t('master.list.rfid.tag.changeHistory'),
          thClass: 'mc-table--50',
          customColumn: true,
        },
      ]
    },
    query() {
      return {
        get: async (opts) => {
          const res = await this.$apollo.query({
            query: GET_RFIDS,
            variables: {
              filter: {
                orderBy: opts.orderBy,
                search: opts.search,
                pagination: {
                  page: opts.pagination.page,
                  show: opts.pagination.show,
                },
              },
            },
            fetchPolicy: 'no-cache',
          })

          res.data.getRfids.getRfids.data = res.data.getRfids.getRfids.data.map((item) => {
            return {
              ...item,
              driver: item.driverId
                ? {
                    userId: item.driverId || null,
                    fullname: item.driver?.fullname || '',
                    isActive: item.driver?.isActive,
                  }
                : null,
              isActive: {
                value: item.isActive ? 'A' : 'I',
                name: item.isActive ? this.$t('global.active') : this.$t('global.nonActive'),
              },
            }
          })
          this.totalActiveRFID = res.data.getRfids.getRfids.rfidStatistic.activated
          this.totalActiveRFIDWithoutDriver = res.data.getRfids.getRfids.rfidStatistic.notAssigned
          this.totalInactiveRFID = res.data.getRfids.getRfids.rfidStatistic.inactivated
          if (
            res.data.getRfids.getRfids.data.length === 0 &&
            (opts.search === '' || opts.search === undefined)
          ) {
            this.isTableDataAvailable = false
          }
          this.isEditing = false
          return res.data.getRfids.getRfids
        },
        update: async (name, oldData, newData) => {
          const input = {
            rfid: name,
            oldData: {
              description: oldData.description,
              driverId: oldData.driver?.userId || null,
              isActive: oldData.isActive.value === 'A' ? true : false,
            },
            newData: {
              description: newData.description,
              driverId: newData['driver.fullname'],
              isActive: newData.isActive === 'A' ? true : false,
            },
          }
          const res = await this.$apollo.mutate({
            mutation: UPDATE_RFID,
            variables: {
              input: input,
            },
          })
          if (!res?.data?.updateRfid?.isSuccessful) {
            if (res.data.updateRfid.message == 'Driver has been assigned to another rfid') {
              this.$mcToast.error(this.$t('master.list.rfid.validation.driverHasBeenAssignedToTag'))
            } else {
              this.$mcToast.error(res.data.updateRfid.message)
            }
            return
          }

          const refetchedData = await this.$apollo.query({
            query: GET_RFIDS,
            variables: {
              filter: {
                rfid: [name],
              },
            },
            fetchPolicy: 'no-cache',
          })

          const rfidData = refetchedData.data.getRfids.getRfids.data[0]
          const resultData = {
            rfid: rfidData.rfid,
            isActive: {
              value: rfidData.isActive ? 'A' : 'I',
              name: rfidData.isActive ? this.$t('global.active') : this.$t('global.nonActive'),
            },
            driver: rfidData.driverId
              ? {
                  userId: rfidData.driverId,
                  fullname: rfidData.driver?.fullname,
                  isActive: rfidData.driver.isActive,
                }
              : null,
            description: rfidData.description,
            assignedDate: rfidData.assignedDate,
          }

          this.listRFIDTags = this.listRFIDTags.map((item) =>
            item.rfid === name ? rfidData : item
          )

          this.isEditing = false
          return resultData
        },
      }
    },
    resourcefulQuery() {
      return {
        getEmployees: async (opts) => {
          const filter = {
            isActive: true,
            isDriver: true,
            search: opts.search,
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
          }

          const res = await this.$apollo.query({
            fetchPolicy: 'no-cache',
            query: GET_EMPLOYEES_PAGINATED,
            variables: {
              filter: filter,
            },
          })
          const data = res.data.employeesPaginated.employeesPaginated
          const result = {
            metadata: {
              total_page: Math.ceil(data.pagination.total / data.pagination.show),
              total_count: data.pagination.total,
              count: data.pagination.show,
              page: opts.page,
            },
            data: {
              paginated_result: data.data,
            },
          }

          // Append inactive driver to the list if current row's driver is inactive
          const editingIndex = this.$refs['table-ref'].isEditingRow
          const rowItem = this.$refs['table-ref'].items[editingIndex]
          const currentItem = this.listRFIDTags.find((item) => item.rfid === rowItem.rfid)
          if (currentItem.driver?.isActive === false && opts.page === 1) {
            result.data.paginated_result.unshift({
              fullname: currentItem.driver.fullname,
              userId: currentItem.driverId,
            })
          }

          return result
        },
        getStatuses: async () => {
          const result = {
            metadata: {
              total_page: 1,
              total_count: 2,
              count: 2,
              page: 1,
            },
            data: {
              paginated_result: [
                {
                  value: 'A',
                  name: this.$t('global.active'),
                },
                {
                  value: 'I',
                  name: this.$t('global.nonActive'),
                },
              ],
            },
          }
          return result
        },
      }
    },
  },
  mounted() {
    this.getAllRFIDTags()
  },
  methods: {
    formatIndexDate,
    refreshTable() {
      this.selectedRFIDTags = []
      this.updateStatus = ''
      this.getAllRFIDTags()

      if (this.$refs['table-ref']) {
        this.$refs['table-ref'].handleUnselectAll()
        this.$refs['table-ref'].getData({
          limit: this.$refs['table-ref'].limit,
          page: 1,
          sorts: this.$refs['table-ref'].stringSorts(),
        })
      }
    },
    showUpdateStatus(status) {
      this.updateStatus = status
      this.$bvModal.show('modal-update-status')
    },
    handleSelectTag(tags) {
      if (Object.entries(tags).length === 0) {
        this.selectedRFIDTags = []
        return
      }
      Object.entries(tags).forEach(([key, value]) => {
        if (value === true) {
          this.selectedRFIDTags = [...new Set([...this.selectedRFIDTags, key])]
        } else {
          this.selectedRFIDTags = this.selectedRFIDTags.filter((rfid) => {
            return rfid !== key
          })
        }
      })
    },
    async updateTagStatuses() {
      const res = await this.$apollo.mutate({
        mutation: UPDATE_RFID_STATUSES,
        variables: {
          input: {
            rfids: this.selectedRFIDTags,
            isActive: this.updateStatus === 'A' ? true : false,
          },
        },
      })
      if (!res?.data?.updateRfidStatuses?.isSuccessful) {
        this.$mcToast.error(res.data.updateRfidStatuses.message)
        this.$bvModal.hide('modal-update-status')
        return
      }
      this.$mcToast.success(this.$t('master.list.rfid.tag.successUpdateStatuses'))
      this.$bvModal.hide('modal-update-status')
      this.refreshTable()
    },
    async getAllRFIDTags() {
      const res = await this.$apollo.query({
        query: GET_RFIDS,
        variables: {
          filter: {
            pagination: {
              page: 1,
              show: 9999,
            },
          },
        },
        fetchPolicy: 'no-cache',
      })
      this.listRFIDTags = res.data.getRfids.getRfids.data
    },

    handleShowHistory(item) {
      if (!this.isEditing) {
        return this.$router.push({
          name: 'Master RFID History Tag',
          params: { id: item.rfid },
        })
      }
    },
    handleChange() {
      this.isEditing = true
    },
    handleEditCancel() {
      this.isEditing = false
    },
  },
}
</script>
